import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';
import SortableTable from './SortableTable';

dayjs.extend(relativeTime);

const formatDate = (value) => {
  if (!value)
    return '—';

  const date = dayjs(value);
  return `${date.format('L LT')} (${date.fromNow()})`;
};

const formatDuration = (ms) => {
  if (ms == null)
    return '—';

  if (ms < 1000)
    return `${ms} ms`;

  const seconds = ms / 1000;
  if (seconds < 60)
    return `${seconds.toFixed(1)} s`;

  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const STATUS_STYLES = {
  success: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
  error: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
  partial: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-800'}`}>
    {status}
  </span>
);

const formatCounts = (counts) => {
  if (!counts || typeof counts !== 'object')
    return '—';

  const parts = [];
  if (counts.inserted != null) parts.push(`+${counts.inserted}`);
  if (counts.scanned != null) parts.push(`scan ${counts.scanned}`);
  if (counts.skippedDuplicates != null) parts.push(`dup ${counts.skippedDuplicates}`);
  if (counts.skippedKnown != null) parts.push(`known ${counts.skippedKnown}`);
  if (counts.skippedFuture != null) parts.push(`fut ${counts.skippedFuture}`);

  return parts.length ? parts.join(' · ') : '—';
};

export default function IndexationRunsTable({ rows, loading }) {
  const { t } = useTranslation();

  const columns = [
    { key: 'source', label: t('Source') },
    { key: 'client', label: t('Client'), rawValue: (r) => r.client || '' },
    { key: 'mode', label: t('Mode') },
    {
      key: 'status',
      label: t('Status'),
      rawValue: (r) => r.status,
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: 'counts',
      label: t('Counts'),
      rawValue: (r) => formatCounts(r.counts),
      render: (r) => <span className="font-mono">{formatCounts(r.counts)}</span>,
    },
    {
      key: 'duration_ms',
      label: t('Duration'),
      filterType: 'number',
      rawValue: (r) => r.duration_ms,
      render: (r) => formatDuration(r.duration_ms),
    },
    {
      key: 'started_at',
      label: t('Started at'),
      filterType: 'date',
      rawValue: (r) => r.started_at,
      render: (r) => formatDate(r.started_at),
    },
    {
      key: 'error',
      label: t('Error'),
      rawValue: (r) => r.error || '',
      render: (r) => (
        <span className="text-red-600 dark:text-red-400 max-w-xs truncate inline-block align-bottom" title={r.error || ''}>
          {r.error || ''}
        </span>
      ),
    },
  ];

  if (loading)
    return <div className="px-4 py-6 text-gray-500 dark:text-gray-400">{t('Loading...')}</div>;

  return (
    <SortableTable
      data={rows}
      columns={columns}
      emptyMessage={t('No data available')}
      initialSortBy={{ key: 'started_at', direction: 'descending' }}
      exportable={true}
      exportFilename="indexation-runs.csv"
      exportRenamable={true}
    />
  );
}
